import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { faBuilding } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as PhoneIcon } from "@assets/icons/phone.svg";

import { Icon } from "@/components";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";
import { AboutSection } from "@/components/IdentityCard/AboutSection";
import { ExpandToggle } from "@/components/IdentityCard/ExpandToggle";

import { CrmIcon } from "@/pages/Private/pages/List/components/CrmIcon";

import { ensureHttpProtocol } from "@/utils/url";

import { Organization } from "../../../Apollo/schema/get-people";
import { handleCopyClick } from "../../../List/utils/handleCopyClick";
import { CompanyHeaderExpanded, companyHeaderHasDetails } from "./CompanyHeaderExpanded";
import { CompanyFindMoreLeads } from "./CompanyFindMoreLeads";

interface CompanyHeaderProps {
	organization?: Organization;
	trackerOrganizationName?: string;
	companyLogoError: boolean;
	setCompanyLogoError: Dispatch<SetStateAction<boolean>>;
	/**
	 * Extension override for the "Find more leads at {company}" button. Forwarded to
	 * {@link CompanyFindMoreLeads}: when set, the button opens the extension's native find-people view
	 * instead of the web {@link GlobalSearchModal}. Omit on the web.
	 */
	onFindMoreLeads?: (company: { name?: string; domain?: string }) => void;
	/**
	 * Outer-spacing override. Defaults to the lead-panel margins; hosts embedding
	 * the card in their own layout (feed run cards) pass their own spacing.
	 */
	className?: string;
	/**
	 * Render the "Find more leads at {company}" button under the card (the
	 * lead-panel surfaces). The feed run cards hide it — discovery does not
	 * belong on the signal feed.
	 */
	showFindMoreLeads?: boolean;
}

const formatEmployeesText = (
	organization: Organization | undefined,
	t: (key: string, options?: Record<string, unknown>) => string
): string | null => {
	if (organization?.employeesFrom && organization?.employeesTo) {
		return t("contacts.leadPanel.identity.employeesRange", {
			from: organization.employeesFrom.toLocaleString(),
			to: organization.employeesTo.toLocaleString(),
		});
	}

	if (organization?.estimatedNumEmployees) {
		return t("contacts.leadPanel.identity.employeesCount", {
			formatted: organization.estimatedNumEmployees.toLocaleString(),
		});
	}

	return null;
};

/**
 * Compact company identity card (unified-prototype v33 `lp-company`): logo,
 * name with the LinkedIn / website / phone / CRM status icons inline, the
 * company-category pill + employee count, and a location line ending in a
 * "See more" expander revealing the description and further company details.
 * Plain Tailwind, inline expansion only — safe inside the Chrome extension's
 * shadow root.
 */
export const CompanyHeader: FunctionComponent<CompanyHeaderProps> = ({
	organization,
	trackerOrganizationName,
	companyLogoError,
	setCompanyLogoError,
	onFindMoreLeads,
	className,
	showFindMoreLeads = true,
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);

	// Company segment (sub-category) first — the top-level bucket ("Prospect", "Partner", …)
	// is only a last resort for legacy rows without segment data.
	const companyCategory =
		organization?.scoreCompanyCategoryResult?.scoreCompanyCategory?.category ||
		organization?.scoreCompanyCategoryResult?.result ||
		organization?.scoreCompanyCategoryResult?.companyCategory;
	const employeesText = formatEmployeesText(organization, t);
	const hasExpandableContent = companyHeaderHasDetails(organization, !!companyCategory);

	return (
		<div className={className ?? "mb-5 mx-3"}>
			{/* flex-1 is inert in the default block wrapper; it lets flex-column
			    hosts (feed run cards) stretch the card to the row height. */}
			<div className="flex flex-1 flex-col border border-gray-200 rounded-lg p-3">
				<div className="flex flex-row items-start gap-2.5">
					<div className="flex h-[42px] min-w-[42px] max-w-[42px] shrink-0 overflow-hidden rounded-lg">
						{organization?.logoUrl && !companyLogoError ? (
							<img
								alt={organization?.name || ""}
								className="h-[42px] w-[42px] object-cover"
								src={organization.logoUrl}
								onError={() => setCompanyLogoError(true)}
							/>
						) : (
							<div className="h-[42px] w-[42px] bg-gray-200 rounded-lg flex items-end justify-center">
								<Icon
									className="h-[32px] w-[32px] flex text-blue-action items-center justify-center"
									icon={faBuilding}
								/>
							</div>
						)}
					</div>

					<div className="flex min-w-0 flex-1 flex-col items-start">
						{/* Name + icon strip cluster together (v33 `lp-name-row`), wrapping as needed */}
						<div className="flex w-full flex-row flex-wrap items-center gap-x-1.5 gap-y-1">
							<span className="max-w-full truncate text-[15px] font-semibold">
								{trackerOrganizationName || organization?.name}
							</span>

							<span className="inline-flex flex-row flex-wrap items-center">
								{organization?.linkedinUrl && (
									<a
										href={ensureHttpProtocol(organization.linkedinUrl)}
										referrerPolicy="no-referrer"
										rel="noreferrer"
										target="_blank"
										onClick={(e) => e.stopPropagation()}
									>
										<LinkedinIcon status="checked" />
									</a>
								)}
								{organization?.websiteUrl && (
									<button
										aria-label="copy website"
										className="cursor-pointer flex h-min-content"
										type="button"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleCopyClick(organization?.websiteUrl ?? "");
										}}
									>
										<Tooltip content={organization?.websiteUrl}>
											<WebsiteIcon status="checked" />
										</Tooltip>
									</button>
								)}
								{organization?.phone && (
									<a
										href={`tel:${organization.phone}`}
										referrerPolicy="no-referrer"
										rel="noreferrer"
										target="_blank"
										onClick={(e) => e.stopPropagation()}
									>
										<SvgIcon className="mr-1 h-[16px] w-auto" svgIcon={PhoneIcon} />
									</a>
								)}
								{organization?.crmOrganizations?.map((crmOrganization) => (
									<Tooltip
										key={crmOrganization.id ?? crmOrganization.referenceId}
										content={crmOrganization.accountOwner || crmOrganization.crmType || ""}
									>
										{crmOrganization.url ? (
											<a
												href={ensureHttpProtocol(crmOrganization.url)}
												rel="noreferrer"
												target="_blank"
												onClick={(e) => e.stopPropagation()}
											>
												<CrmIcon
													crmType={crmOrganization.crmType}
													syncError={crmOrganization.syncError}
													syncStatus={crmOrganization.syncStatus}
													url={crmOrganization.url ?? ""}
												/>
											</a>
										) : (
											<CrmIcon
												crmType={crmOrganization.crmType}
												syncError={crmOrganization.syncError}
												syncStatus={crmOrganization.syncStatus}
												url={crmOrganization.url ?? ""}
											/>
										)}
									</Tooltip>
								))}
							</span>
						</div>

						{(companyCategory || organization?.industry || employeesText) && (
							<div className="mt-1 flex w-full flex-row flex-wrap items-center gap-x-1.5 gap-y-1">
								{companyCategory ? (
									<Tooltip
										content={organization?.scoreCompanyCategoryResult?.reason || ""}
										disabled={!organization?.scoreCompanyCategoryResult?.reason}
									>
										<span className="text-xxs font-semibold border border-tag-warning-border bg-tag-warning-light rounded-full px-1.5 py-px text-tag-warning-dark max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis">
											{companyCategory}
										</span>
									</Tooltip>
								) : (
									organization?.industry && (
										<span className="text-xs font-medium text-text-secondary">
											{organization.industry}
										</span>
									)
								)}
								{employeesText && (
									<span className="text-[11px] text-text-tertiary">
										{(companyCategory || organization?.industry) && (
											<span className="text-text-quaternary">· </span>
										)}
										{employeesText}
									</span>
								)}
							</div>
						)}

						{(organization?.city || organization?.country || hasExpandableContent) && (
							<div className="mt-0.5 flex w-full flex-row flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-text-tertiary">
								<span className="truncate">
									{organization?.city && organization?.country
										? `${organization.city}, `
										: organization?.city || ""}
									{organization?.country}
								</span>
								{hasExpandableContent && (
									<ExpandToggle
										expanded={expanded}
										showLabelKey="contacts.leadPanel.identity.seeMore"
										onToggle={() => setExpanded((value) => !value)}
									/>
								)}
							</div>
						)}
					</div>
				</div>

				{expanded && (
					<div className="mt-2.5 flex flex-col gap-3 rounded-lg bg-brand-white p-3">
						<AboutSection text={organization?.shortDescription || organization?.seoDescription} />
						<CompanyHeaderExpanded organization={organization} showIndustry={!!companyCategory} />
					</div>
				)}
			</div>

			{showFindMoreLeads && (
				<CompanyFindMoreLeads
					companyName={trackerOrganizationName || organization?.name}
					organization={organization}
					onFindMoreLeads={onFindMoreLeads}
				/>
			)}
		</div>
	);
};
